import type { PersistenceState } from './useSandboxPersistence';

/**
 * Where the project stands against what was saved, as the header shows it (`data-testid="save-state"`):
 * - `clean`: nothing changed since the project was loaded;
 * - `dirty`: it changed since it was last saved or loaded;
 * - `saving`: a save is in flight;
 * - `saved`: the last save took, and nothing changed since;
 * - `error`: the last save (or load) failed — its message says why.
 */
export type SaveState = 'clean' | 'dirty' | 'saving' | 'saved' | 'error';

/** The save state, and the words a person reads for it. A save in flight comes first, then a failure, then changes. */
export function saveStateOf(
  persistence: Pick<PersistenceState, 'isSaving' | 'error' | 'hasUnsavedChanges' | 'savedRevision'>,
): { state: SaveState; text: string } {
  if (persistence.isSaving) return { state: 'saving', text: 'Saving…' };
  if (persistence.error) return { state: 'error', text: persistence.error };
  if (persistence.hasUnsavedChanges) return { state: 'dirty', text: 'Unsaved changes' };
  if (persistence.savedRevision !== null) return { state: 'saved', text: `Saved — revision ${persistence.savedRevision}` };
  return { state: 'clean', text: 'No unsaved changes' };
}
